import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { completeInvite, getInviteContext } from "../features/invite/api";
import type { InviteContext } from "../features/invite/api";
import { FilePicker } from "../components/FilePicker";
import { fileTooLargeError } from "../lib/upload";
import { missingRequiredLabels } from "../lib/visitor-fields";
import { ApiError } from "../lib/api-client";
import { useCompanyBranding } from "../lib/useCompanyBranding";
import { isValidIndianPhone } from "../lib/validation";
import "../App.css";

type FormState = {
  fullName: string;
  phone: string;
  companyName: string;
  notes: string;
  idType: string;
  idNumber: string;
  hasLaptop: boolean;
  laptopMac: string;
  laptopSerial: string;
};

const INITIAL: FormState = {
  fullName: "",
  phone: "",
  companyName: "",
  notes: "",
  idType: "AADHAAR",
  idNumber: "",
  hasLaptop: false,
  laptopMac: "",
  laptopSerial: "",
};

export function PublicInvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const [context, setContext] = useState<InviteContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [photo, setPhoto] = useState<File | null>(null);
  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const { logoUrl } = useCompanyBranding(context?.tenant_company_code);

  useEffect(() => {
    getInviteContext(token)
      .then(setContext)
      .catch((error: unknown) => {
        setLoadError(
          error instanceof ApiError
            ? error.message
            : "This invite link is invalid or has expired.",
        );
      });
  }, [token]);

  async function submit() {
    if (!form.fullName.trim()) {
      setSubmitError("Full name is required");
      return;
    }
    const missing = missingRequiredLabels(context?.required_fields ?? [], {
      phone: !!form.phone.trim(),
      company: !!form.companyName.trim(),
      notes: !!form.notes.trim(),
      id_number: !!form.idNumber.trim(),
      photo: !!photo,
      id_document: !!idDocument,
      laptop: form.hasLaptop && !!form.laptopMac.trim() && !!form.laptopSerial.trim(),
    });
    if (missing.length > 0) {
      setSubmitError(`Please provide: ${missing.join(", ")}`);
      return;
    }
    const sizeError =
      fileTooLargeError(photo, "Your photo") ?? fileTooLargeError(idDocument, "ID document photo");
    if (sizeError) {
      setSubmitError(sizeError);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await completeInvite(token, {
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
        companyName: form.companyName.trim(),
        notes: form.notes.trim(),
        idType: form.idType,
        idNumber: form.idNumber.trim(),
        hasLaptop: form.hasLaptop,
        laptopMac: form.laptopMac.trim(),
        laptopSerial: form.laptopSerial.trim(),
        photo,
        idDocument,
      });
      setDone(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to submit your details");
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <main className="invite-shell">
        <section className="hero">
          <h1>Invite link unavailable</h1>
          <p className="subtitle">{loadError}</p>
        </section>
      </main>
    );
  }

  if (!context) {
    return (
      <main className="invite-shell">
        <section className="hero">
          <h1>Loading invite...</h1>
        </section>
      </main>
    );
  }

  if (done || context.already_completed) {
    return (
      <main className="invite-shell">
        <section className="hero">
          <h1>Thanks -- you're all set</h1>
          <p className="subtitle">
            Your details were submitted to {context.host_name}. Security will review your visit
            before you arrive.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="invite-shell">
      <section className="hero">
        <p className="eyebrow">
          {logoUrl ? (
            <img src={logoUrl} alt={context.tenant_company_code} className="company-logo" />
          ) : (
            <span className="brand-wordmark">
              0todeploy <span className="brand-vms">VMS</span>
            </span>
          )}
          <span className="role-chip">Visitor Invitation</span>
        </p>
        <h1>Hosted by {context.host_name}</h1>
        <p className="subtitle">
          Purpose: {context.purpose}. Please complete your details below before you arrive.
        </p>
      </section>

      <form
        className="panel invite-form-grid"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <input
          value={form.fullName}
          onChange={(event) => setForm((prev) => ({ ...prev, fullName: event.target.value }))}
          placeholder="Full name *"
          required
        />
        <div className="field-with-hint">
          <input
            value={form.phone}
            onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
            placeholder="Phone (10-digit mobile)"
            inputMode="numeric"
          />
          {form.phone.trim() && !isValidIndianPhone(form.phone) ? (
            <span className="field-hint">Enter a valid 10-digit mobile number</span>
          ) : null}
        </div>
        <input
          value={form.companyName}
          onChange={(event) => setForm((prev) => ({ ...prev, companyName: event.target.value }))}
          placeholder="Company"
        />
        <textarea
          value={form.notes}
          onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
          placeholder="Anything else we should know?"
          rows={3}
        />

        <div className="checkin-fields">
          <select
            className="search-input"
            value={form.idType}
            onChange={(event) => setForm((prev) => ({ ...prev, idType: event.target.value }))}
          >
            <option value="AADHAAR">Aadhaar</option>
            <option value="PASSPORT">Passport</option>
            <option value="DRIVING_LICENSE">Driving License</option>
            <option value="OTHER">Other ID</option>
          </select>
          <input
            value={form.idNumber}
            onChange={(event) => setForm((prev) => ({ ...prev, idNumber: event.target.value }))}
            placeholder="ID number (kept confidential)"
            autoComplete="off"
          />
        </div>

        <FilePicker label="Your photo" file={photo} onChange={setPhoto} />
        <FilePicker label="ID document photo" file={idDocument} onChange={setIdDocument} />

        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={form.hasLaptop}
            onChange={(event) => setForm((prev) => ({ ...prev, hasLaptop: event.target.checked }))}
          />
          I'm bringing a laptop
        </label>
        {form.hasLaptop ? (
          <div className="checkin-fields">
            <input
              value={form.laptopMac}
              onChange={(event) => setForm((prev) => ({ ...prev, laptopMac: event.target.value }))}
              placeholder="Laptop MAC address"
            />
            <input
              value={form.laptopSerial}
              onChange={(event) => setForm((prev) => ({ ...prev, laptopSerial: event.target.value }))}
              placeholder="Laptop serial / system number"
            />
          </div>
        ) : null}

        {submitError ? <p className="error-banner">{submitError}</p> : null}

        <button className="refresh-btn action-submit" type="submit" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit my details"}
        </button>
      </form>
    </main>
  );
}
